import { useEffect, useState } from "react";

import Dialog from "@rio-cloud/rio-uikit/Dialog";
import Button from "@rio-cloud/rio-uikit/Button";
import Select from "@rio-cloud/rio-uikit/Select";
import DatePicker from "@rio-cloud/rio-uikit/DatePicker";
import TimePicker from "@rio-cloud/rio-uikit/TimePicker";

import { fetchSchedulingByChassisList } from "../../../../../../../api/fetchScheduling";
import { checkServiceBayAvailability } from "../../../../../../../utils/serviceBayAvailabilityUtils";

import { Occurrence } from "../../../../../../../models/occurrence";
import { ServiceBay, ServiceBaySchedule } from "../../../../../../../models/serviceBay";
import VehicleIcon from "../../../../../../VehicleIcon";

import ErrorDialog from "../../../../../../dialogs/ErrorDialog";
import ConfirmDialog from "../../../../../../dialogs/ConfirmDialog";

export interface IServiceBayScheduleForm {
  serviceBay?: string;
  startDate?: Date;
  startTime?: Date;
  endDate?: Date;
  endTime?: Date;
  occurrence?: string;
}

interface IServiceBayScheduleModalProps {
  scheduleServiceBays: ServiceBaySchedule[];
  serviceBays: ServiceBay[];
  isOpen: boolean;
  onClose: () => void;
  onDelete: () => void;

  occurrence?: Occurrence;
  setServiceBaySchedule: (data: IServiceBayScheduleForm) => void;
}

export const ServiceBayScheduleDialog = ({
  scheduleServiceBays,
  serviceBays,
  isOpen,
  onClose,
  onDelete,

  occurrence,
  setServiceBaySchedule,
}: IServiceBayScheduleModalProps) => {
  const [isView, setIsView] = useState(false);

  const [isServiceBayAvailable, setIsServiceBayAvailable] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<IServiceBayScheduleForm>({});

  // Para evitar erro de renderização do TimePicker
  const [endTimePickerValue, setEndTimePickerValue] = useState<string>("");

  const getNextHour = () => {
    const now = new Date();
    now.setMinutes(0);
    now.setHours(now.getHours() + 1);
    return now;
  }

  const getScheduleDate = async (): Promise<Date | undefined> => {
    if (!occurrence?.vehicleChassis || !occurrence?.scheduleNumber) return undefined;
    const schedulings = await fetchSchedulingByChassisList([occurrence?.vehicleChassis!]);
    const scheduling = schedulings.find((scheduling) => scheduling?.scheduleNumber === occurrence?.scheduleNumber);
    return scheduling?.date;
  }

  useEffect(() => {
    const fillForm = async () => {
      if (occurrence?.serviceBaySchedule) {
        setForm({
          serviceBay: occurrence?.serviceBaySchedule.serviceBay?.id,
          startDate: occurrence?.serviceBaySchedule.startDate,
          startTime: occurrence?.serviceBaySchedule.startDate,
          endDate: occurrence?.serviceBaySchedule.endDate,
          endTime: occurrence?.serviceBaySchedule.endDate,
          occurrence: occurrence?.id,
        });
      } else {
        const scheduleDate = await getScheduleDate();
        setForm({
          serviceBay: undefined,
          startDate: scheduleDate || getNextHour(),
          startTime: scheduleDate || getNextHour(),
          endDate: undefined,
          endTime: undefined,
          occurrence: occurrence?.id,
        });
      }
    }

    fillForm();
    setIsView(!!occurrence?.serviceBaySchedule);
  }, [isOpen]);

  const handleChange = (key: string, value: string | Date | undefined) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    checkServiceBayAvailabilityFromForm({ ...form, [key]: value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    if (!form.serviceBay || !form.startDate || !form.startTime || !form.endDate || !form.endTime) {
      setError("Preencha todos os campos obrigatórios");
      setIsLoading(false);
      return;
    }

    try {
      setServiceBaySchedule({
        serviceBay: form.serviceBay,
        occurrence: form.occurrence,
        startDate: form.startDate,
        startTime: form.startTime,
        endDate: form.endDate,
        endTime: form.endTime,
      });
      onClose();
    } catch (error: any) {
      setError(error.message || "Erro ao criar agendamento");
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Verifica disponibilidade baseada no formulário (mantém compatibilidade)
   */
  const checkServiceBayAvailabilityFromForm = (formToCheck: IServiceBayScheduleForm) => {
    if (!formToCheck.serviceBay || !formToCheck.startDate || !formToCheck.startTime || !formToCheck.endDate || !formToCheck.endTime) {
      setIsServiceBayAvailable(false);
      return;
    }

    // Converte as datas do formulário para Date objects
    let startDate: Date;
    let endDate: Date;

    try {
      // Processa a data de início
      startDate = formToCheck.startDate;

      // Processa a data de fim
      endDate = formToCheck.endDate;

      // Adiciona os horários às datas
      const [startHour, startMinute] = (formToCheck.startTime?.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }) || "").split(":").map(Number);
      const [endHour, endMinute] = (formToCheck.endTime?.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }) || "").split(":").map(Number);

      startDate.setHours(startHour, startMinute, 0, 0);
      endDate.setHours(endHour, endMinute, 0, 0);

      // Verifica disponibilidade
      const isAvailable = checkServiceBayAvailability(scheduleServiceBays, formToCheck.serviceBay, startDate, endDate);
      setIsServiceBayAvailable(isAvailable);
    } catch (error) {
      console.error("Erro ao processar datas:", error);
      setIsServiceBayAvailable(false);
    }
  };

  return (
    <>
      <Dialog show={isOpen} onClose={onClose} bsSize="md">
        <form onSubmit={handleSubmit}>
          <Dialog.Title title={isView ? "Agenda de Box" : "Criar agenda de Box"} />

          <Dialog.Body>
            <div>
              <div className={`w-full border border-[#DADCE0] dark:border-neutral-700 rounded p-8 flex items-center justify-between cursor-default`}>
                <div className="flex items-center gap-6 text-[16px] w-full">
                  <VehicleIcon 
                    chassis={occurrence?.vehicleChassis} 
                    size={64}
                  />
                  <div className="flex flex-col w-full">
                    <span className="font-semibold text-size-14">
                      {occurrence?.vehicleModel || occurrence?.vehicleName || "Sem nome"}
                    </span>
                    <span className="flex justify-between w-full text-size-14">
                      <span>
                        Chassi: {occurrence?.vehicleChassis ?? "-"}
                      </span>
                      <div className="flex gap-8">
                        <span className="mt-[-8px]">
                          <strong>
                            Ocorrência: {occurrence?.scheduleNumber ?? "-"}
                          </strong>
                        </span>
                      </div>
                    </span>
                  </div>
                </div>
              </div>

              <div className="pt-5">
                <label className="block text-size-16 mb-1 font-semibold">
                  Box *
                </label>
                {isView ? (
                  <div className="text-[14px] text-[#98A2B3] dark:text-neutral-400 mt-2">
                    {occurrence?.serviceBaySchedule?.serviceBay?.name || ""}
                  </div>
                ) : (
                  <Select
                    className="form-group-lg icon-right"
                    options={serviceBays.map((serviceBay) => ({
                      id: serviceBay.id!,
                      label: serviceBay.name,
                      value: serviceBay.id,
                    }))}
                    value={form.serviceBay ? [form.serviceBay] : undefined}
                    onChange={(item) =>
                      handleChange("serviceBay", item?.id || "")
                    }
                    disabled={isView}
                  />
                )}
              </div>

              <div className="grid grid-cols-2 gap-6 mt-[15px]">
                <div>
                  <label className="block text-size-16 mb-1 font-semibold">
                    Data Início *
                  </label>
                  {isView ? (
                    <div className="text-[14px] text-[#98A2B3] dark:text-neutral-400 mt-2">
                      {occurrence?.serviceBaySchedule?.startDate?.toLocaleDateString("pt-BR", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                      })}
                    </div>
                  ) : (
                    <DatePicker
                      className="form-group-lg icon-right"
                      value={form.startDate || ""}
                      onChange={(val) => handleChange("startDate", typeof val === "string" ? new Date(val) : val.toDate())}
                      inputProps={{ placeholder: "Escolha a data" }}
                      dateFormat="DD/MM/YYYY"
                      timeFormat={false}
                      locale="pt-br"
                    />
                  )}
                </div>
                <div>
                  <label className="block text-size-16 mb-1 font-semibold">
                    Hora início *
                  </label>
                  {isView ? (
                    <div className="text-[14px] text-[#98A2B3] dark:text-neutral-400 mt-2">
                      {occurrence?.serviceBaySchedule?.startDate?.toLocaleTimeString("pt-BR", {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </div>
                  ) : (
                    <TimePicker
                      inputProps={{ className: "timePicker" }}
                      className="form-group-lg icon-right"
                      value={form.startTime?.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }) || ""}
                      onChange={(val) => {
                        const [hours, minutes] = val.split(":");
                        handleChange("startTime", new Date((form.startDate ?? new Date()).setHours(parseInt(hours), parseInt(minutes), 0, 0)));
                      }}
                      showIcon
                    />
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-6 mt-[15px]">
                <div>
                  <label className="block text-size-16 mb-1 font-semibold">
                    Data Fim *
                  </label>
                  {isView ? (
                    <div className="text-[14px] text-[#98A2B3] dark:text-neutral-400 mt-2">
                      {occurrence?.serviceBaySchedule?.endDate?.toLocaleDateString("pt-BR", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                      })}
                    </div>
                  ) : (
                    <DatePicker
                      className="form-group-lg icon-right"
                      value={form.endDate || ""}
                      onChange={(val) => handleChange("endDate", typeof val === "string" ? new Date(val) : val.toDate())}
                      inputProps={{ placeholder: "Escolha a data" }}
                      dateFormat="DD/MM/YYYY"
                      timeFormat={false}
                      locale="pt-br"
                    />
                  )}
                </div>
                <div>
                  <label className="block text-size-16 mb-1 font-semibold">
                    Hora fim *
                  </label>
                  {isView ? (
                    <div className="text-[14px] text-[#98A2B3] dark:text-neutral-400 mt-2">
                      {occurrence?.serviceBaySchedule?.endDate?.toLocaleTimeString("pt-BR", {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </div>
                  ) : (
                    <TimePicker
                      inputProps={{ className: "timePicker" }}
                      className="form-group-lg icon-right"
                      value={endTimePickerValue}
                      onChange={(val) => {
                        const [hours, minutes] = val.split(":");
                        handleChange("endTime", new Date((form.endDate ?? new Date()).setHours(parseInt(hours), parseInt(minutes), 0, 0)));
                        setEndTimePickerValue(val);
                      }}
                      showIcon
                    />
                  )}
                </div>
              </div>

              {!isView && form.serviceBay &&
                (!isServiceBayAvailable ? (
                  <div className="flex justify-end mt-3">
                    <div className="text-[14px] text-[#DC2626] dark:text-red-300 bg-[#FEF2F2] dark:bg-red-900/30 border border-[#FCA5A5] dark:border-red-800 px-3 py-1 inline-block rounded-full mt-3">
                      Agenda indisponível
                    </div>
                  </div>
                ) : (
                  <div className="flex justify-end mt-3">
                    <div className="text-[14px] text-[#98A2B3] dark:text-green-300 bg-[#ECFDF3] dark:bg-green-900/30 border border-[#ABEFC6] dark:border-green-800 px-3 py-1 inline-block rounded-full mt-3">
                      Agenda disponível
                    </div>
                  </div>
                ))}
            </div>
          </Dialog.Body>

          <Dialog.Footer>
            <div className="flex justify-between w-full py-[5px]">
              <button
                type="button"
                className={`btn-link text-[14px] text-[#98A2B3] dark:text-neutral-400 mt-2 ${isDeleting ? "btn btn-loading disabled" : ""} ${isView ? "btn btn-danger" : ""}`}
                onClick={() => setIsDeleting(true)}
                disabled={isDeleting || !isView}
              >
                Excluir agenda
              </button>
              {!isView ? (
                <Button
                  className={`btn btn-primary ${isLoading ? "btn-loading disabled" : ""}`}
                  type="submit"
                  disabled={
                    isLoading ||
                    !!scheduleServiceBays.find(
                      (schedule) => schedule.occurrence?.id === form.occurrence
                    ) || !isServiceBayAvailable
                  }
                >
                  Salvar
                </Button>
              ) : (
                <div />
              )}
            </div>
          </Dialog.Footer>
        </form>
      </Dialog>

      <ConfirmDialog
        show={isDeleting}
        title="Excluir agendamento"
        body="Tem certeza que deseja excluir o agendamento?"
        onClose={() => setIsDeleting(false)}
        onConfirm={async () => {
          setIsDeleting(true);
          try {
            onDelete();
            onClose();
          } catch (error: any) {
            setError(error.message || "Erro ao deletar agendamento");
          } finally {
            setIsDeleting(false);
          }
        }}
      />

      <ErrorDialog
        show={!!error}
        title="Erro no agendamento"
        body={error}
        onClose={() => setError(null)}
      />
    </>
  );
};
